import { computed, onScopeDispose, ref, type ComputedRef, type Ref } from "vue"
import { useDisplay } from "vuetify"

/**
 * Semantic viewport flags for the app shell.
 *
 * The app is built for desks, but construction reports and hourly-wage reports are filled in on
 * site — on a tablet, standing up, often one-handed. Rather than sprinkling raw `width < 1234`
 * checks around (the four components that used `useDisplay` before this all invented their own),
 * every responsive decision goes through the two thresholds below so the whole app changes shape
 * at the same two widths.
 *
 * The deliberate target is **tablet**: everything from 600px up is a first-class layout. Below
 * 600px (`isPhone`, or `max-width: 599.98px` in CSS) the compact layouts get a last round of
 * trimming — overview tables stack their rows, stacked insets shrink, header controls wrap — so a
 * record can still be looked up and corrected on a phone. See docs/responsive-layout.md.
 */

/**
 * Below this the navigation drawer stops reserving a column and slides in over the content
 * instead. Vuetify's `lg`, so a 1280px laptop keeps the permanent drawer while every tablet
 * (landscape iPad = 1180px, portrait = 820px) hands its ~256px back to the content.
 */
export const NAV_OVERLAY_BREAKPOINT = 1264

/**
 * Below this, the project's report tables are replaced by touch-friendly cards and toolbars are
 * allowed to wrap. Vuetify's `md`. (The overview tables stack only below 600px; in between they
 * scroll sideways inside their card.) Between this and {@link NAV_OVERLAY_BREAKPOINT} the tables still fit
 * because the drawer has already given its space back.
 */
export const COMPACT_BREAKPOINT = 960

/**
 * True on touch-primary devices. Module-level so the `matchMedia` query is created once, no matter
 * how many components ask; SSR/jsdom without `matchMedia` degrades to `false`.
 */
const coarsePointer = ref(false)
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  const query = window.matchMedia("(pointer: coarse)")
  coarsePointer.value = query.matches
  query.addEventListener("change", event => (coarsePointer.value = event.matches))
}

export interface ResponsiveLayout {
  /** Live viewport width in px. */
  width: Ref<number>
  /** Navigation drawer overlays the content instead of reserving a column. */
  isNavOverlay: ComputedRef<boolean>
  /** Tables become card lists, multi-column form rows stack, toolbars wrap. */
  isCompact: ComputedRef<boolean>
  /** Phone-sized. Used only to drop non-essential chrome, never to hide data. */
  isPhone: ComputedRef<boolean>
  /** The pointer is a finger. Independent of width — a 1920px touch panel is still touch. */
  isTouch: Ref<boolean>
}

export function useResponsiveLayout(): ResponsiveLayout {
  const { width } = useDisplay()

  return {
    width,
    isNavOverlay: computed(() => width.value < NAV_OVERLAY_BREAKPOINT),
    isCompact: computed(() => width.value < COMPACT_BREAKPOINT),
    isPhone: computed(() => width.value < 600),
    isTouch: coarsePointer,
  }
}

/**
 * Runs `onClose` whenever the viewport grows past the overlay threshold, so a drawer left open on
 * a tablet does not linger as a stuck overlay when the device is rotated or the window widened.
 */
export function useCloseOnDesktop(onClose: () => void): void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return

  const query = window.matchMedia(`(min-width: ${NAV_OVERLAY_BREAKPOINT}px)`)
  const handler = (event: MediaQueryListEvent) => {
    if (event.matches) onClose()
  }
  query.addEventListener("change", handler)
  onScopeDispose(() => query.removeEventListener("change", handler))
}
